import React, { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, TrendingUp, DollarSign, PieChart, Loader2, AlertCircle } from "lucide-react";
import * as api from "../../api";

/* -----------------------------------------------------------------------------
 * SalesPanel — admin "Ganancias" tab. Reads from Supabase (`sales_daily` view +
 * recent `sales` rows) via the backend proxy so the service_role key stays on
 * the server. Auto-refreshes every 15s + on tab focus + manual refresh.
 * --------------------------------------------------------------------------- */

const REFRESH_MS = 15_000;

const fmt = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-VE", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" });
  } catch { return iso; }
};

function KpiCard({ label, value, sublabel, accent }) {
  const border = {
    revenue: "border-cyan-500/40 bg-cyan-500/5",
    cost: "border-amber-500/40 bg-amber-500/5",
    profit: "border-emerald-500/40 bg-emerald-500/5",
  }[accent] || "border-[var(--ag7-border-soft)]";
  return (
    <div className={`rounded-2xl border ${border} p-4 space-y-1`} data-testid={`kpi-${accent}`}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-2xl sm:text-3xl font-black text-white font-mono tabular-nums">{value}</p>
      {sublabel && <p className="text-[11px] font-mono text-zinc-500">{sublabel}</p>}
    </div>
  );
}

export function SalesPanel() {
  const [daily, setDaily] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([
        api.getSalesDaily(),
        api.getSalesRecent(50),
      ]);
      setDaily(d.rows || []);
      setRecent(r.rows || []);
      setError(null);
      setLastSync(new Date());
    } catch (err) {
      setError(err?.message || "Error al leer Supabase");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  // Aggregations
  const now = new Date();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  // IMPORTANT: force **Venezuela local time (UTC-4)** so the "HOY" bucket
  // aligns with the backend rollup (which groups by VE-local `sale_date`)
  // regardless of the admin's device timezone. Using `toISOString()` or
  // `new Date().getDate()` would shift by up to a day for anyone on a
  // non-VE clock, causing "HOY = $0" bugs when it's already tomorrow in UTC.
  const toVeIso = (d) => {
    const ve = new Date(d.getTime() - 4 * 60 * 60 * 1000); // shift to UTC-4
    return ve.toISOString().slice(0, 10);
  };
  const todayStr = toVeIso(now);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const sumRange = (fromDate) => {
    const cutoff = toVeIso(fromDate);
    return daily
      .filter(d => d.sale_date >= cutoff)
      .reduce((acc, d) => {
        acc.revenue += Number(d.revenue_usd || 0);
        acc.cost    += Number(d.cost_usd || 0);
        acc.profit  += Number(d.profit_usd || 0);
        acc.items   += Number(d.items_sold || 0);
        return acc;
      }, { revenue: 0, cost: 0, profit: 0, items: 0 });
  };

  const today   = (daily.find(d => d.sale_date === todayStr)) || { revenue_usd: 0, cost_usd: 0, profit_usd: 0, items_sold: 0 };
  const last7   = sumRange(sevenDaysAgo);
  const last30  = sumRange(thirtyDaysAgo);

  return (
    <div className="space-y-6" data-testid="sales-panel">
      {/* Header + refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Ganancias & Ventas
          </h2>
          <p className="text-[11px] font-mono text-zinc-500 mt-0.5">
            Sincronizado desde Supabase · Auto-refresh cada 15s
            {lastSync && <> · Última: {fmtDate(lastSync.toISOString())}</>}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="sales-refresh"
          className="px-3 py-2 rounded-xl bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 text-xs font-bold flex items-center gap-1.5 hover:bg-emerald-600/30 disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refrescar
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-950/30 border border-red-500/40 text-red-200 text-xs">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-mono uppercase tracking-widest text-[10px] mb-0.5">Error de conexión</p>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* KPI grid: HOY */}
      <section>
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">HOY</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard label="Ingresos" value={fmt(today.revenue_usd)} sublabel={`${today.items_sold} ventas`} accent="revenue" />
          <KpiCard label="Costos" value={fmt(today.cost_usd)} accent="cost" />
          <KpiCard label="Ganancia" value={fmt(today.profit_usd)} accent="profit" />
        </div>
      </section>

      {/* KPI grid: 7d / 30d */}
      <section>
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">ÚLTIMOS 7 DÍAS</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard label="Ingresos" value={fmt(last7.revenue)} sublabel={`${last7.items} ventas`} accent="revenue" />
          <KpiCard label="Costos" value={fmt(last7.cost)} accent="cost" />
          <KpiCard label="Ganancia" value={fmt(last7.profit)} accent="profit" />
        </div>
      </section>

      <section>
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">ÚLTIMOS 30 DÍAS</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard label="Ingresos" value={fmt(last30.revenue)} sublabel={`${last30.items} ventas`} accent="revenue" />
          <KpiCard label="Costos" value={fmt(last30.cost)} accent="cost" />
          <KpiCard label="Ganancia" value={fmt(last30.profit)} accent="profit" />
        </div>
      </section>

      {/* Daily table */}
      <section>
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
          <PieChart className="w-3.5 h-3.5" /> Diario (últimos 30 días)
        </h3>
        <div className="rounded-2xl border border-[var(--ag7-border-soft)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="daily-table">
              <thead className="bg-ag7-surface-2/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-right px-3 py-2">Ventas</th>
                  <th className="text-right px-3 py-2">Ingresos</th>
                  <th className="text-right px-3 py-2">Costos</th>
                  <th className="text-right px-3 py-2">Ganancia</th>
                </tr>
              </thead>
              <tbody>
                {daily.slice(0, 30).map(d => (
                  <tr key={d.sale_date} className="border-t border-[var(--ag7-border-soft)]/60 text-zinc-300">
                    <td className="px-3 py-2 font-mono">{d.sale_date}</td>
                    <td className="px-3 py-2 text-right">{d.items_sold}</td>
                    <td className="px-3 py-2 text-right text-cyan-300">{fmt(d.revenue_usd)}</td>
                    <td className="px-3 py-2 text-right text-amber-300">{fmt(d.cost_usd)}</td>
                    <td className="px-3 py-2 text-right text-emerald-300 font-bold">{fmt(d.profit_usd)}</td>
                  </tr>
                ))}
                {daily.length === 0 && !loading && (
                  <tr><td colSpan={5} className="text-center py-6 text-zinc-500 font-mono">Sin datos aún</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Recent transactions */}
      <section>
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
          <DollarSign className="w-3.5 h-3.5" /> Transacciones Recientes (50)
        </h3>
        <div className="rounded-2xl border border-[var(--ag7-border-soft)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="recent-sales-table">
              <thead className="bg-ag7-surface-2/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Pedido</th>
                  <th className="text-left px-3 py-2">Juego</th>
                  <th className="text-left px-3 py-2">Paquete</th>
                  <th className="text-right px-3 py-2">Venta</th>
                  <th className="text-right px-3 py-2">Costo</th>
                  <th className="text-right px-3 py-2">Ganancia</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => {
                  // Rows may come from AG7 backend (new schema) OR from the
                  // standalone "Registro Diario" HTML (legacy schema). Try
                  // the new fields first, fall back to the legacy names.
                  const gameName = r.game_name || r.game || "—";
                  const pkgName  = r.package_name || r.package || "—";
                  const sale = Number(r.sale_amount ?? r.sell_price ?? 0);
                  const cost = Number(r.cost_amount ?? r.buy_price  ?? 0);
                  const profit = Number(r.profit_amount ?? r.gain ?? (sale - cost));
                  return (
                    <tr key={r.id} className="border-t border-[var(--ag7-border-soft)]/60 text-zinc-300">
                      <td className="px-3 py-2 font-mono whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-2 font-mono text-cyan-300">
                        {r.order_number ? `#${String(r.order_number).padStart(4, "0")}` : "—"}
                      </td>
                      <td className="px-3 py-2">{gameName}</td>
                      <td className="px-3 py-2">{pkgName}</td>
                      <td className="px-3 py-2 text-right text-cyan-300">{fmt(sale)}</td>
                      <td className="px-3 py-2 text-right text-amber-300">{fmt(cost)}</td>
                      <td className="px-3 py-2 text-right text-emerald-300 font-bold">{fmt(profit)}</td>
                    </tr>
                  );
                })}
                {recent.length === 0 && !loading && (
                  <tr><td colSpan={7} className="text-center py-6 text-zinc-500 font-mono">Sin transacciones aún</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
